/**
 * Shared utilities for TodoWrite tool handling.
 * Used by both streaming and reconstruction paths to ensure consistent behavior.
 */

import type { TodoTask } from "@/components/execution/AgentTodoList";

/**
 * Simple hash function for generating stable IDs from content.
 * Uses djb2 hash algorithm for fast, consistent hashing.
 */
function hashString(str: string): string {
	let hash = 5381;
	for (let i = 0; i < str.length; i++) {
		hash = (hash * 33) ^ str.charCodeAt(i);
	}
	return (hash >>> 0).toString(36);
}

/**
 * Ensure all todo tasks have valid IDs.
 * Generates stable IDs for tasks that don't have them.
 * This is critical for React key stability during streaming and re-renders.
 */
export function ensureTaskIds(tasks: TodoTask[]): TodoTask[] {
	return tasks.map((task, index) => {
		if (task.id) {
			return task;
		}
		// Generate stable ID from content hash + position
		const contentHash = hashString(task.content);
		const id = `todo-${index}-${contentHash}`;
		return { ...task, id };
	});
}

/**
 * Parse plan text into structured todo tasks.
 * Handles numbered lists, bullet points, and checkboxes.
 * Used by both live streaming and historical reconstruction.
 */
export function parsePlanTasks(planText: string): TodoTask[] {
	const tasks: TodoTask[] = [];
	const lines = planText.split("\n").filter((line) => line.trim());

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		// Match numbered lists, bullet points, or checkboxes
		const match = line.match(/^(\d+|[-*✓✗○●])\s*\.?\s*(.+)$/);
		if (match) {
			const content = match[2].trim();
			let status: "pending" | "in_progress" | "completed" = "pending";

			if (line.startsWith("✓") || line.includes("[x]")) {
				status = "completed";
			} else if (line.startsWith("●") || line.includes("[>]")) {
				status = "in_progress";
			}

			// Generate stable ID from content hash + position
			const contentHash = hashString(content);
			const id = `todo-${i}-${contentHash}`;

			tasks.push({
				id,
				content,
				status,
			});
		}
	}

	return tasks;
}
